import { useCallback, useMemo } from 'react';
import { odata } from '@bingads-webui/http-util';
import type { ActivityDefaultInterface } from '@bingads-webui-clientcenter/instrumentation/src/types';
import { useOdataOptions } from './use-odata-options';

// --- Shared snapshot value types ---

export interface SourcedValue {
  Value: string | null;
  Source?: {
    PrimarySource?: 'Website' | 'SocialMedia' | 'Document' | 'LLM' | 'UserEdit' | 'Model' | 'Multi-source';
    SourceUrl?: string | null;
    Explanation?: string;
  };
  Confidence?: number;
}

export interface SourcedItemList<T> {
  Items: T[];
  Source?: SourcedValue['Source'];
  Confidence?: number;
}

// --- AIGC enums (mirror BingAds.Api.Service.Contracts) ---

/** Snapshot category produced by AIGC (`SnapshotCategory` enum on the C# side). */
export type SnapshotCategory = 'BrandAssets' | 'Offerings' | 'Customers' | 'Location';

/** Input source AIGC consumed (`InputSourceName` enum on the C# side). */
export type InputSourceName = 'BusinessLinks' | 'Documents' | 'Images';

/**
 * Task status (`AIGCTaskStatus` enum).
 *
 * `TaskExpired` is canonically delivered as an HTTP 400 with a TaskExpired
 * code in the error body (see DESIGN.md §AIGC error contract). Listing it
 * here as a body-status value is defensive: if MT ever surfaces it inline
 * in the response body, the polling code routes it to the terminal Failed
 * branch instead of treating it as InProgress and spinning indefinitely.
 * AB#10747343.
 */
export type AIGCTaskStatus = 'InProgress' | 'Completed' | 'Failed' | 'TaskExpired';

/** Polling next-action hint (`NextAction` enum). */
export type NextAction = 'Poll' | 'Done' | 'Retry';

/** Failed-category record returned by both Snapshot and Campaign APIs. */
export interface FailedCategory {
  Category: string;
  Code: string;
  Message: string;
  Retryable: boolean;
  DetailCode?: string;
}

// --- Snapshot shape (PascalCase, mirrors the C# AIGC contracts) ---

/** Single image entry inside `Brand.Logos.Items` and `Brand.Images.Items`. */
export interface BrandMediaItem {
  Url: string;
  AltText?: string;
  Format?: string;
  Width?: number;
  Height?: number;
  /** Populated for user-uploaded assets so Brand Kit sync can reuse the id. */
  AssetId?: string;
}

export interface BrandSnapshot {
  Logos: SourcedItemList<BrandMediaItem>;
  Images: SourcedItemList<BrandMediaItem>;
  BrandVoice: SourcedValue;
  TermExclusions: SourcedItemList<string>;
  /**
   * Per the C# `BrandSnapshot.MessageRestrictions` contract this is a list,
   * not a single value. (The Brand Kit OData model uses the plural
   * "MessagingRestrictions" — the rename happens on the write boundary.)
   */
  MessageRestrictions: SourcedItemList<string>;
}

/** Single business-link entry from the wire. */
export interface BusinessLink {
  Platform: string;
  Handle?: string;
  Url: string;
  Origin?: 'UserProvided' | 'Discovered' | string;
}

export interface BusinessSnapshot {
  Name: SourcedValue;
  Location: SourcedValue;
  BusinessLinks: BusinessLink[];
  AdvertisingGoals: SourcedValue;
  Offerings: SourcedValue;
  Customers: SourcedValue;
}

export interface BusinessBrandSnapshot {
  Brand: BrandSnapshot;
  Business: BusinessSnapshot;
}

export interface InputSourceStatus {
  Source: InputSourceName;
  Status: 'InProgress' | 'Succeeded' | 'Failed';
  Message?: string | null;
  Retryable?: boolean | null;
}

// --- API: POST generate-business-snapshot ---

/**
 * Wire shape on the C# side is Pascal-case (`UploadedFile { FileName, FileUrl }`).
 * The OData layer normalizes case on serialize.
 */
export interface UploadedFile {
  fileName: string;
  fileUrl: string;
}

export interface GenerateBusinessSnapshotRequest {
  businessUrl: string;
  locale?: string;
  additionalLinks?: string[];
  uploadedImages?: UploadedFile[];
  uploadedDocumentBlobNames?: string[];
}

/**
 * AIGC envelope wrapping every successful response. Maps to the C#
 * `AIGCResponse<T>` type. Errors come back as a top-level
 * `List<AdsApiError>` with HTTP 400 instead of this envelope.
 *
 * The wire shape is PascalCase (`Data`, `Errors`).
 */
export interface AIGCResponse<T> {
  Data: T;
  Errors: unknown;
}

export interface GenerateBusinessSnapshotAccepted {
  TaskId: string;
  Categories: SnapshotCategory[];
  InputSources?: InputSourceName[];
}

/**
 * Real wire shape from `POST AccountsV2/GenerateBusinessSnapshot`:
 *   { Data: { TaskId, Categories, InputSources }, Errors: null }
 */
export type GenerateBusinessSnapshotPostResponse =
  AIGCResponse<GenerateBusinessSnapshotAccepted>;

/**
 * Real wire shape from `GET AccountsV2/GetBusinessSnapshotResult`:
 *   { Data: { TaskId, Status, NextAction, ProcessedCategories, ...,
 *             Snapshot: { Brand, Business }, Warnings }, Errors: null }
 */
export interface GenerateBusinessSnapshotResult {
  TaskId: string;
  Status: AIGCTaskStatus;
  NextAction: NextAction;
  ProcessedCategories: SnapshotCategory[];
  ProcessingCategories: SnapshotCategory[];
  FailedCategories: FailedCategory[];
  InputStatus?: InputSourceStatus[];
  GenerationSummary?: {
    InputsUsed: string[];
    InputsFailed: string[];
    CategoriesFailed: string[];
    /** Free-text describing precedence applied (e.g. "Documents > BusinessLinks > Images"). */
    PrecedenceApplied?: string;
    Explanation: string;
  };
  /** Present when `Status === 'Completed'`. */
  Snapshot?: BusinessBrandSnapshot;
  Warnings?: {
    Field: string;
    Code: string;
    Message: string;
  }[];
}

export type GenerateBusinessSnapshotPollResponse =
  AIGCResponse<GenerateBusinessSnapshotResult>;

/**
 * Subset of the canonical default-logger activity interface we need for
 * request/response tracing — `trace` for request/response, `error` for
 * failures, both with `(message, api?, requestId?)`. Picked from
 * `ActivityDefaultInterface` so any default-scenario activity is
 * structurally assignable; passing the concrete activity is optional so
 * callers without a logger pay no instrumentation cost.
 */
export type AigcRequestActivity = Pick<ActivityDefaultInterface, 'trace' | 'error'>;

const API_NAME = 'odataapi/campaign/v2/accountsv2/generatebusinesssnapshot';

export const useGenerateBusinessSnapshot = () => {
  const [odataOptions, { cid, aid }] = useOdataOptions('Advertiser/V2');

  const url = useMemo(() => (
    `AccountsV2/GenerateBusinessSnapshot(CustomerId=${cid},AccountId=${aid})`
  ), [cid, aid]);

  return useCallback((
    request: GenerateBusinessSnapshotRequest,
    activity?: AigcRequestActivity
  ) => {
    const requestBody = {
      businessUrl: request.businessUrl,
      locale: request.locale || 'en-US',
      ...(request.additionalLinks && { additionalLinks: request.additionalLinks }),
      ...(request.uploadedImages && { uploadedImages: request.uploadedImages }),
      ...(request.uploadedDocumentBlobNames && {
        uploadedDocumentBlobNames: request.uploadedDocumentBlobNames,
      }),
    };

    // Log request + response/error from a single jQuery `complete` callback
    // so all three trace lines carry the same x-ms-requestid (extracted
    // from the response headers).
    return odata.post({
      ...odataOptions,
      url,
      data: requestBody,
      complete: (jqXHR: any, textStatus: string) => {
        if (!activity) return;
        const requestId =
          (jqXHR && typeof jqXHR.getResponseHeader === 'function')
            ? (jqXHR.getResponseHeader('x-ms-requestid') ?? undefined)
            : undefined;
        const isSuccess = textStatus === 'success'
          || textStatus === 'notmodified'
          || textStatus === 'nocontent';

        activity.trace(
          `[PMaxLiteSignup][AgenticOnboarding] GenerateBusinessSnapshot request: ${JSON.stringify(requestBody)}`,
          API_NAME,
          requestId
        );

        if (isSuccess) {
          const responseBody = jqXHR?.responseJSON;
          activity.trace(
            `[PMaxLiteSignup][AgenticOnboarding] GenerateBusinessSnapshot response: taskId=${responseBody?.Data?.TaskId ?? ''}`,
            API_NAME,
            requestId
          );
        } else {
          const errorBody = jqXHR?.responseJSON ?? jqXHR?.responseText ?? jqXHR?.statusText ?? textStatus;
          // Without httpStatus, a dropped request (0), an expired token (401)
          // and a gateway error (5xx) all serialize to the same `"error"`.
          activity.error(
            `[PMaxLiteSignup][AgenticOnboarding] GenerateBusinessSnapshot failed: httpStatus=${jqXHR?.status} textStatus=${textStatus} ${JSON.stringify(errorBody)}`,
            API_NAME,
            requestId
          );
        }
      },
    } as any) as Promise<GenerateBusinessSnapshotPostResponse>;
  }, [odataOptions, url]);
};
